import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Divider,
  Drawer,
  Grid2,
  IconButton,
  Link,
  Table,
  TableBody,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import AppTableHeaderCell from "../../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTableRowCell from "../../../../../../views/components/common/pages/AppTableRowCell";
import LoaderOverlay from "../../../../../../views/components/common/pages/AppLoader";
import FormTextField from "../../../../../../views/components/common/form/FormTextField";
import { useDailyTasksFieldPermissions } from "./useDailyTasksFieldPermissions";
import { formatDateWithoutTime } from "../../../../../../views/components/common/functions/formatDate";
import { DailyTasksStore } from "../../../../stores/dashboards/DailyTasksStore";
import { trackDailyTasksAction } from "../../../../../../analytics/dailyTasksTracking";
import {
  DAILY_TASKS_STAGE_TABS,
  DailyTasksDetailResult,
  INVENTORY_STAGES,
  PurchaseOrderDrawerDto,
} from "../../../../../../models/Dashboard/DailyTasks/DailyTasks";
import {
  getModernPageColors,
  modernDrawerBodySx,
  modernDrawerHeaderSx,
} from "../../../../../../styles/modernListingPageSx";
import {
  filenameFromUrl,
  IdLink,
  ImagePreviewFrame,
  purchaseRequestDetailsPath,
} from "./DailyTasksMedia";
import MockupDrawerBody from "./MockupDrawerBody";
import FileDrawerBody from "./FileDrawerBody";
import PrDrawerBody from "./PrDrawerBody";
import PoDrawerBody from "./PoDrawerBody";
import ProjectDrawerBody from "./ProjectDrawerBody";
import RawDrawerBody from "./RawDrawerBody";
import PrintDrawerBody from "./PrintDrawerBody";
import PrintInDrawerBody from "./PrintInDrawerBody";
import ProductCommentsSection from "./ProductCommentsSection";
import {
  DailyTasksEmptyState,
  DailyTasksSectionHeader,
} from "./DailyTasksEmptyState";
import { dailyTasksChipSx } from "./dailyTasksChips";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import LocalMallOutlinedIcon from "@mui/icons-material/LocalMallOutlined";
import BrushOutlinedIcon from "@mui/icons-material/BrushOutlined";

const DRAWER_Z_OFFSET = 4000;

interface DailyTasksDrawerProps {
  store: DailyTasksStore;
  embedded?: boolean;
  onBack?: () => void;
}

const formatDate = (value: string | Date | null | undefined): string => {
  if (!value) return "-";
  return formatDateWithoutTime(value) || "-";
};

const formatHours = (hours: number | null | undefined): string => {
  if (hours == null || !Number.isFinite(Number(hours))) return "-";
  const value = Math.round(Number(hours));
  if (value < 24) return `${value}h`;
  const days = Math.floor(value / 24);
  const rem = value % 24;
  return rem ? `${days}d ${rem}h` : `${days}d`;
};

const pipelineLabel = (task: string): string => {
  const match = DAILY_TASKS_STAGE_TABS.find(
    (tab) =>
      tab.id === task || tab.id.replace(/_/g, "") === task.replace(/_/g, ""),
  );
  if (match) return match.label;
  return task.replace(/_/g, " ");
};

const PrintingAndPoSection: React.FC<{
  detail: DailyTasksDetailResult;
  skuSearch: string;
  setSkuSearch: (value: string) => void;
  filteredPos: PurchaseOrderDrawerDto[];
  renderPoAccordion: (po: PurchaseOrderDrawerDto) => React.ReactNode;
  showPrinting?: boolean;
}> = ({
  detail,
  skuSearch,
  setSkuSearch,
  filteredPos,
  renderPoAccordion,
  showPrinting = true,
}) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  return (
    <>
      {showPrinting ? (
        <>
          <DailyTasksSectionHeader
            title="Printing files"
            count={detail.printing_files.length}
          />
          {detail.printing_files.length === 0 ? (
            <DailyTasksEmptyState
              icon={ImageOutlinedIcon}
              title="No printing files yet"
              description="CDR / printing files uploaded for this product will appear here."
              sx={{ mb: 2, mt: 0 }}
            />
          ) : (
            <Grid2 container spacing={1.5} columns={12} sx={{ mb: 2 }}>
              {detail.printing_files.map((file) => (
                <Grid2 key={file.id} size={{ xs: 12, sm: 6 }}>
                  <Box
                    sx={{
                      border: `1px solid ${colors.softBorder}`,
                      borderRadius: 2,
                      overflow: "hidden",
                      bgcolor: colors.white,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <ImagePreviewFrame
                      src={file.image_url}
                      alt="printing file"
                      filename={filenameFromUrl(
                        file.image_url,
                        `printing-file-${file.id}.jpg`,
                      )}
                    />
                    <Box sx={{ p: 1.25 }}>
                      <CustomTypography
                        variant="body3"
                        fontWeight={500}
                        sx={{ display: "block" }}
                      >
                        {file.admin_name || "Unknown"} ·{" "}
                        {formatDate(file.created_date)}
                      </CustomTypography>
                      <CustomTypography variant="body3" fontWeight={500}>
                        {Boolean(file.sent_to_printer)
                          ? `Sent ${formatDate(file.sent_to_printer_date)}`
                          : "Not sent to printer"}
                      </CustomTypography>
                    </Box>
                  </Box>
                </Grid2>
              ))}
            </Grid2>
          )}
        </>
      ) : null}

      <DailyTasksSectionHeader
        title="Purchase orders"
        count={detail.purchase_orders.length}
        sx={{ mt: showPrinting ? 0 : 2 }}
      />
      {detail.purchase_orders.length > 0 ? (
        <>
          <FormTextField
            marginBottom={1.5}
            id="sku-search"
            placeholder="Search SKU or PO"
            value={skuSearch}
            onChange={(_, value) => {
              try {
                setSkuSearch(value || "");
              } catch (error) {
                console.error("Failed to update SKU search:", error);
              }
            }}
          />
          {filteredPos.map(renderPoAccordion)}
        </>
      ) : (
        <DailyTasksEmptyState
          icon={LocalMallOutlinedIcon}
          title="No purchase orders yet"
          description="Issued POs for this product will show up here with quantities and status."
          sx={{ mt: 0 }}
        />
      )}
    </>
  );
};

const DailyTasksDrawer: React.FC<DailyTasksDrawerProps> = ({
  store,
  embedded = false,
  onBack,
}) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const pageColors = getModernPageColors(theme);
  const { canViewDispatchDate } = useDailyTasksFieldPermissions();
  const [skuSearch, setSkuSearch] = useState("");
  const [debouncedSkuSearch, setDebouncedSkuSearch] = useState("");
  const [expandedPoId, setExpandedPoId] = useState<number | false>(false);

  const open = embedded ? Boolean(store.selectedRow) : store.productDrawerOpen;
  const detail = store.detail;
  const isMockupStage =
    store.filters.stage === "mockup_upload" ||
    store.filters.stage === "mockup_approval";
  const isFileStage =
    store.filters.stage === "file_upload" ||
    store.filters.stage === "cdr_to_printer";
  const isPrStage = store.filters.stage === "pr_creation";
  const isPoStage = store.filters.stage === "po_creation";
  const isProjectsStage = store.filters.stage === "projects";
  const isRawStage = store.filters.stage === "raw_inward";
  const isPrintStage = store.filters.stage === "print_outward";
  const isPrintInStage = store.filters.stage === "print_inward";
  const stageTab = DAILY_TASKS_STAGE_TABS.find(
    (tab) => tab.id === store.filters.stage,
  );

  useEffect(() => {
    if (!open || !store.selectedRow) return;
    void store.fetchDetail();
  }, [
    open,
    store.selectedRow?.order_id,
    store.selectedRow?.product_id,
    store.filters.stage,
    store,
  ]);

  useEffect(() => {
    setSkuSearch("");
    setDebouncedSkuSearch("");
    const firstIncomplete = (detail?.purchase_orders ?? []).find(
      (po) => po.totals.pending > 0,
    );
    setExpandedPoId(
      firstIncomplete?.po_id ?? detail?.purchase_orders?.[0]?.po_id ?? false,
    );
  }, [detail]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSkuSearch(skuSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [skuSearch]);

  const filteredPos = useMemo(() => {
    const term = debouncedSkuSearch.trim().toLowerCase();
    const pos = detail?.purchase_orders ?? [];
    if (!term) return pos;
    return pos
      .map((po) => ({
        ...po,
        sku_lines: po.sku_lines.filter(
          (line) =>
            line.sku.toLowerCase().includes(term) ||
            (line.title || "").toLowerCase().includes(term),
        ),
      }))
      .filter(
        (po) =>
          po.sku_lines.length > 0 || po.po_number?.toLowerCase().includes(term),
      );
  }, [detail?.purchase_orders, debouncedSkuSearch]);

  if (!store.selectedRow) return null;

  const copyPo = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch (error) {
      console.error(error);
    }
  };

  const renderPoAccordion = (po: PurchaseOrderDrawerDto) => {
    const commanderHref =
      detail?.commander_path || store.selectedRow?.commander_path || "";
    const prHref =
      po.pr_id != null
        ? purchaseRequestDetailsPath(
            po.pr_id,
            detail?.order_id ?? store.selectedRow?.order_id,
          )
        : commanderHref;
    const headerLabel = `${po.vendor_name || "Vendor"} · ${po.totals.received}/${po.totals.ordered}`;
    return (
      <Accordion
        key={po.po_id}
        expanded={expandedPoId === po.po_id}
        onChange={(_, expanded) => setExpandedPoId(expanded ? po.po_id : false)}
        sx={{
          mb: 1,
          borderRadius: "8px !important",
          border: `1px solid ${colors.softBorder}`,
          boxShadow: "none",
          bgcolor: colors.white,
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
              pr: 1,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {prHref ? (
                <IdLink href={prHref} label={`#${po.po_number || po.po_id}`} />
              ) : (
                <CustomTypography variant="body2" fontWeight={500}>
                  #{po.po_number || po.po_id}
                </CustomTypography>
              )}
              <CustomTypography
                variant="body3"
                fontWeight={500}
                sx={{ color: colors.greyTextColor }}
              >
                {headerLabel}
              </CustomTypography>
            </Box>
            {po.totals.pending > 0 ? (
              <Chip
                size="small"
                label={`${po.totals.pending} pending`}
                sx={dailyTasksChipSx(theme, "warn")}
              />
            ) : (
              <Chip
                size="small"
                label="Complete"
                sx={dailyTasksChipSx(theme, "ok")}
              />
            )}
            {(isRawStage || isPrintInStage) && po.qc_on_hold ? (
              <Chip
                size="small"
                label="On hold"
                sx={dailyTasksChipSx(theme, "bad")}
              />
            ) : null}
            {(isRawStage || isPrintInStage) &&
            po.qc_in_progress &&
            !po.qc_on_hold ? (
              <Chip
                size="small"
                label="QC"
                sx={dailyTasksChipSx(theme, "info")}
              />
            ) : null}
            {(isRawStage || isPrintInStage) && !po.invoice_received ? (
              <Chip
                size="small"
                label="No invoice"
                sx={dailyTasksChipSx(theme, "warn")}
              />
            ) : null}
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                void copyPo(String(po.po_number || po.po_id));
              }}
            >
              <ContentCopyIcon fontSize="inherit" />
            </IconButton>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <CustomTypography
            variant="body3"
            fontWeight={500}
            sx={{ color: colors.greyTextColor }}
          >
            Expected {formatDate(po.events.expected_at)} · First{" "}
            {formatDate(po.events.first_at)} · Last{" "}
            {formatDate(po.events.last_at)}
            {po.events.by_admin ? ` · ${po.events.by_admin}` : ""}
          </CustomTypography>
          <Table size="small" sx={{ mt: 1.25 }}>
            <TableHead>
              <TableRow>
                <AppTableHeaderCell
                  title="SKU"
                  minWidth="120px"
                  txtAlign="left"
                  fontWeight={500}
                  fontSize="0.75rem"
                />
                <AppTableHeaderCell
                  title="Title"
                  minWidth="100px"
                  txtAlign="left"
                  fontWeight={500}
                  fontSize="0.75rem"
                />
                <AppTableHeaderCell
                  title="Ordered"
                  minWidth="72px"
                  fontWeight={500}
                  fontSize="0.75rem"
                />
                <AppTableHeaderCell
                  title="Received"
                  minWidth="72px"
                  fontWeight={500}
                  fontSize="0.75rem"
                />
                <AppTableHeaderCell
                  title="Rejected"
                  minWidth="72px"
                  fontWeight={500}
                  fontSize="0.75rem"
                />
                <AppTableHeaderCell
                  title="Outward"
                  minWidth="72px"
                  fontWeight={500}
                  fontSize="0.75rem"
                />
                <AppTableHeaderCell
                  title="Pending"
                  minWidth="72px"
                  fontWeight={500}
                  fontSize="0.75rem"
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {po.sku_lines.length === 0 ? (
                <TableRow>
                  <AppTableRowCell
                    title="No SKU lines"
                    fontWeight={500}
                    fontSize="0.8rem"
                  />
                </TableRow>
              ) : (
                po.sku_lines.map((line) => (
                  <TableRow key={line.sku}>
                    <AppTableRowCell
                      textAlign="left"
                      fontWeight={500}
                      fontSize="0.8rem"
                    >
                      <IdLink
                        href={`/products/${store.selectedRow?.product_id}`}
                        label={line.sku}
                        fontSize="0.8rem"
                      />
                    </AppTableRowCell>
                    <AppTableRowCell
                      title={line.title || "-"}
                      textAlign="left"
                      fontWeight={500}
                      fontSize="0.8rem"
                    />
                    <AppTableRowCell
                      title={String(line.ordered)}
                      fontWeight={500}
                      fontSize="0.8rem"
                    />
                    <AppTableRowCell
                      title={String(line.received)}
                      fontWeight={500}
                      fontSize="0.8rem"
                    />
                    <AppTableRowCell
                      title={String(line.rejected)}
                      fontWeight={500}
                      fontSize="0.8rem"
                    />
                    <AppTableRowCell
                      title={String(line.sent)}
                      fontWeight={500}
                      fontSize="0.8rem"
                    />
                    <AppTableRowCell
                      title={String(line.pending)}
                      fontWeight={500}
                      fontSize="0.8rem"
                      color={
                        line.pending > 0 ? colors.errorRed : colors.navyText
                      }
                      sx={{}}
                    />
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </AccordionDetails>
      </Accordion>
    );
  };

  const handleClose = () => {
    if (embedded) {
      store.closeDrawer();
      return;
    }
    store.closeProductDrawer();
  };

  const header = (
    <Box sx={modernDrawerHeaderSx(theme)}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 0.5,
          minWidth: 0,
        }}
      >
        {embedded && onBack ? (
          <IconButton
            onClick={onBack}
            size="small"
            aria-label="Back to records"
            title="Back to records"
            sx={{ mt: 0.25 }}
          >
            <ArrowBackIcon />
          </IconButton>
        ) : null}
        <Box sx={{ minWidth: 0 }}>
          <CustomTypography
            variant="caption"
            sx={{
              color: pageColors.subtitle,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              fontWeight: 600,
            }}
          >
            {stageTab?.label || "Daily tasks"}
          </CustomTypography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
              mt: 0.25,
            }}
          >
            <CustomTypography
              variant="heading5"
              sx={{ color: pageColors.title, wordBreak: "break-word" }}
            >
              {detail?.brand_name || store.selectedRow.brand_name || "Order"}
            </CustomTypography>
            <IdLink
              href={
                detail?.commander_path ||
                store.selectedRow.commander_path ||
                `/projects/${store.selectedRow.order_id}`
              }
              label={`#${store.selectedRow.order_id}`}
              fontSize="0.95rem"
              onNavigate={() => {
                trackDailyTasksAction("link_order_open", store.filters, {
                  orderId: store.selectedRow?.order_id,
                  context: { order_id: store.selectedRow?.order_id },
                });
              }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mt: 0.5,
              flexWrap: "wrap",
            }}
          >
            <CustomTypography
              variant="body2"
              sx={{ color: pageColors.subtitle }}
            >
              {detail?.product_name || store.selectedRow.product_name}
            </CustomTypography>
            <IdLink
              href={`/products/${store.selectedRow.product_id}`}
              label={`#${store.selectedRow.product_id}`}
              fontSize="0.8rem"
              onNavigate={() => {
                trackDailyTasksAction("link_product_open", store.filters, {
                  productId: store.selectedRow?.product_id,
                  orderId: store.selectedRow?.order_id,
                  context: {
                    product_id: store.selectedRow?.product_id,
                    order_id: store.selectedRow?.order_id,
                  },
                });
              }}
            />
          </Box>
        </Box>
      </Box>
      <Box
        sx={{ display: "flex", gap: 1.5, alignItems: "center", flexShrink: 0 }}
      >
        {detail?.commander_path || store.selectedRow.commander_path ? (
          <Link
            href={detail?.commander_path || store.selectedRow.commander_path}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            onClick={() => {
              trackDailyTasksAction("link_order_open", store.filters, {
                orderId: store.selectedRow?.order_id,
                context: { order_id: store.selectedRow?.order_id },
              });
            }}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              color: colors.purple,
              fontWeight: 500,
              fontSize: "0.85rem",
              whiteSpace: "nowrap",
            }}
          >
            {INVENTORY_STAGES.includes(store.filters.stage)
              ? "Go to Inventory Ops"
              : "Go to Sourcing Ops"}
            <OpenInNewIcon sx={{ fontSize: "0.95rem" }} />
          </Link>
        ) : null}
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </Box>
    </Box>
  );

  const body = (
    <Box
      sx={{
        ...modernDrawerBodySx(),
        flex: 1,
        minHeight: 0,
        overflow: "auto",
      }}
    >
      <LoaderOverlay loading={store.drawerLoading} />
      {store.drawerErrorMessage ? (
        <Alert severity="error">{store.drawerErrorMessage}</Alert>
      ) : null}

      {detail && isMockupStage ? <MockupDrawerBody detail={detail} /> : null}
      {detail && isFileStage ? <FileDrawerBody detail={detail} /> : null}
      {detail && isPrStage ? <PrDrawerBody detail={detail} /> : null}
      {detail && isPoStage ? <PoDrawerBody detail={detail} /> : null}
      {detail && isProjectsStage ? <ProjectDrawerBody detail={detail} /> : null}
      {detail && isRawStage ? <RawDrawerBody detail={detail} /> : null}

      {detail && isPrintStage ? <PrintDrawerBody detail={detail} /> : null}
      {detail && isPrintInStage ? <PrintInDrawerBody detail={detail} /> : null}

      {detail &&
      !isMockupStage &&
      !isFileStage &&
      !isPrStage &&
      !isPoStage &&
      !isRawStage &&
      !isPrintStage &&
      !isPrintInStage &&
      !isProjectsStage ? (
        <>
          {canViewDispatchDate &&
          detail.days_to_dispatch != null &&
          detail.days_to_dispatch < 0 ? (
            <Alert
              severity="error"
              sx={{
                py: 0.5,
                bgcolor: `${colors.errorRed}12`,
                color: colors.errorRed,
                "& .MuiAlert-icon": { color: colors.errorRed },
              }}
            >
              Dispatch overdue by {Math.abs(detail.days_to_dispatch)} day
              {Math.abs(detail.days_to_dispatch) === 1 ? "" : "s"}
            </Alert>
          ) : null}
          <Grid2 container spacing={1.5} columns={12}>
            {(
              [
                ["Type", detail.order_type || "-"],
                ["Start", formatDate(detail.start_date)],
                ...(canViewDispatchDate
                  ? ([
                      ["Dispatch", formatDate(detail.dispatch_date)],
                      [
                        "Days to dispatch",
                        String(detail.days_to_dispatch ?? "-"),
                      ],
                    ] as Array<[string, string]>)
                  : []),
                ["Qty", String(detail.ordered_quantity)],
                ["Stage TAT", formatHours(detail.stage_tat_hours)],
                ["Total TAT", formatHours(detail.total_tat_hours)],
                ["PM", detail.pm_poc || "Unassigned"],
                ["Sales", detail.sales_poc || "-"],
              ] as Array<[string, string]>
            ).map(([label, value]) => {
              const overdueMeta =
                label === "Days to dispatch" &&
                detail.days_to_dispatch != null &&
                detail.days_to_dispatch < 0;
              return (
                <Grid2 key={label} size={{ xs: 6, sm: 4, md: 3 }}>
                  <CustomTypography
                    variant="body3"
                    fontWeight={500}
                    sx={{ color: colors.greyTextColor }}
                  >
                    {label}
                  </CustomTypography>
                  <CustomTypography
                    variant="body2"
                    fontWeight={500}
                    sx={{
                      color: overdueMeta ? colors.errorRed : colors.navyText,
                    }}
                  >
                    {value}
                  </CustomTypography>
                </Grid2>
              );
            })}
          </Grid2>

          <CustomTypography variant="heading7" fontWeight={600}>
            Pipeline
          </CustomTypography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {detail.pipeline.map((step) => (
              <Chip
                key={`${step.task}-${step.stage}`}
                size="small"
                label={pipelineLabel(step.task)}
                sx={dailyTasksChipSx(theme, step.complete ? "ok" : "neutral")}
              />
            ))}
          </Box>

          <Divider />

          <DailyTasksSectionHeader
            title="Mockups"
            count={detail.mockups.length}
          />
          {detail.mockups.length === 0 ? (
            <DailyTasksEmptyState
              icon={BrushOutlinedIcon}
              title="No mockups yet"
              description="Uploaded mockups for this product will appear here."
              sx={{ mt: 0 }}
            />
          ) : (
            <Grid2 container spacing={1.5} columns={12}>
              {detail.mockups.map((mockup) => (
                <Grid2 key={mockup.id} size={{ xs: 12, sm: 6 }}>
                  <Box
                    sx={{
                      border: `1px solid ${colors.softBorder}`,
                      borderRadius: 2,
                      overflow: "hidden",
                      bgcolor: colors.white,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <ImagePreviewFrame
                      src={mockup.image_url}
                      alt="mockup"
                      filename={filenameFromUrl(
                        mockup.image_url,
                        `mockup-${mockup.id}.jpg`,
                      )}
                    />
                    <Box sx={{ p: 1.25 }}>
                      <CustomTypography variant="body3" fontWeight={500}>
                        {mockup.uploaded_by || "Unknown"} ·{" "}
                        {formatDate(mockup.created_date)}
                      </CustomTypography>
                      <CustomTypography
                        variant="body3"
                        fontWeight={500}
                        sx={{ display: "block" }}
                      >
                        {Boolean(mockup.approved)
                          ? `Approved by ${mockup.approver_name || "-"}`
                          : Boolean(mockup.changes_requested)
                            ? "Changes requested"
                            : Boolean(mockup.is_rejected)
                              ? `Rejected${mockup.rejection_note ? `: ${mockup.rejection_note}` : ""}`
                              : "Pending approval"}
                      </CustomTypography>
                      {mockup.comments.map((comment) => (
                        <CustomTypography
                          key={comment.id}
                          variant="body3"
                          fontWeight={500}
                          sx={{
                            display: "block",
                            mt: 0.5,
                            color: colors.mutedSlateText,
                          }}
                        >
                          {comment.admin_name || comment.user_name || "Comment"}
                          : {comment.comment}
                        </CustomTypography>
                      ))}
                    </Box>
                  </Box>
                </Grid2>
              ))}
            </Grid2>
          )}

          <PrintingAndPoSection
            detail={detail}
            skuSearch={skuSearch}
            setSkuSearch={setSkuSearch}
            filteredPos={filteredPos}
            renderPoAccordion={renderPoAccordion}
          />
        </>
      ) : null}

      {detail ? (
        <ProductCommentsSection comments={detail.product_comments} />
      ) : null}
    </Box>
  );

  if (embedded) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
          flex: 1,
          bgcolor: pageColors.pageBg,
        }}
      >
        {header}
        {body}
      </Box>
    );
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      sx={{ zIndex: theme.zIndex.drawer + DRAWER_Z_OFFSET }}
      PaperProps={{
        sx: {
          width: {
            xs: "100%",
            sm: isMockupStage ? 720 : 640,
            md: isMockupStage ? 840 : 760,
          },
          maxWidth: "100vw",
          height: "100%",
          maxHeight: "100dvh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          bgcolor: pageColors.pageBg,
        },
      }}
      ModalProps={{
        keepMounted: true,
        sx: {
          zIndex: (muiTheme) => muiTheme.zIndex.drawer + DRAWER_Z_OFFSET,
        },
      }}
    >
      {header}
      {body}
    </Drawer>
  );
};

export default observer(DailyTasksDrawer);
